<template>
    <div>
        <!-- Step 1 -->
        <transition name="fade" mode="out-in">
            <div v-if="step == 1">
                <h4 class="mb-3">Calcolo Retta</h4>
                <form @submit.prevent="submitStep1">
                    <div class="p-4 p-md-5 bg-white border rounded">
                        <!-- Nome -->
                        <div class="row mb-3">
                            <label for="firstName" class="col-md-4 col-form-label text-md-end">Nome</label>
                            <div class="col-12 col-md-8">
                                <input type="text" class="form-control" id="firstName" v-model="form.first_name" :readonly="first_name" required>
                            </div>
                        </div>
                        <!-- Cognome -->
                        <div class="row mb-3">
                            <label for="lastName" class="col-md-4 col-form-label text-md-end">Cognome</label>
                            <div class="col-12 col-md-8">
                                <input type="text" class="form-control" id="lastName" v-model="form.last_name" :readonly="last_name" required>
                            </div>
                        </div>
                        <!-- Codice Fiscale -->
                        <div class="row mb-3">
                            <label for="fiscalCode" class="col-md-4 col-form-label text-md-end">Codice Fiscale</label>
                            <div class="col-12 col-md-8">
                                <input type="text" class="form-control" :class="{ 'is-invalid': !validation.fiscal_code }" id="fiscalCode" v-model="form.fiscal_code" v-uppercase :readonly="fiscal_code || validation.promo_code == 'success'" required>
                                <div class="invalid-feedback" v-if="!validation.fiscal_code">
                                    Codice Fiscale non valido o non corrispondente al nome e/o al cognome.
                                </div>
                            </div>
                        </div>

                        <hr/>

                        <!-- Corso di Laurea -->
                        <div class="row mb-3" v-if="!course">
                            <label for="course" class="col-md-4 col-form-label text-md-end">Corso di Laurea</label>
                            <div class="col-12 col-md-8">
                                <select
                                    class="form-select"
                                    aria-label="Corso di Laurea"
                                    id="course"
                                    v-model="form.course"
                                    required
                                    @change="changeCourse"
                                >
                                    <option :value="null" disabled>Seleziona...</option>
                                    <option v-for="(course, index) in helpers.courses" :key="index" :value="course">
                                        {{ course.name }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <!-- Tipo di Ingresso -->
                        <div class="row mb-3">
                            <label for="entrance" class="col-md-4 col-form-label text-md-end">Tipo di Ingresso</label>
                            <div class="col-12 col-md-8">
                                <select class="form-select" aria-label="Tipo di Ingresso" id="entrance" v-model="form.entrance" required>
                                    <option :value="null" disabled>Seleziona...</option>
                                    <option v-for="(entrance, index) in helpers.entrances" :key="index" :value="entrance">
                                        {{ entrance.label }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <!-- Doppia Laurea -->
                        <div class="row mb-3">
                            <label for="doppia_laurea" class="col-md-4 col-form-label text-md-end">Doppia Laurea</label>
                            <div class="col-12 col-md-8">
                                <select class="form-select" aria-label="Doppia Laurea" id="doppia_laurea" v-model="form.doppia_laurea" required>
                                    <option :value="null" disabled>Seleziona...</option>
                                    <option :value="0">Iscrizione Unica</option>
                                    <option :value="1">Già iscritto presso questo Ateneo</option>
                                    <option :value="2">Già iscritto presso altro Ateneo</option>
                                </select>
                                <div v-if="form.doppia_laurea == 2" class="mt-2">
                                    <v-select
                                        :options="helpers.universities"
                                        v-model="form.doppia_laurea_ateneo"
                                        :clearable="false"
                                        :required="form.doppia_laurea == 2"
                                        placeholder="Seleziona Ateneo"
                                    />
                                </div>
                            </div>
                        </div>
                        <!-- Anno Accademico -->
                        <div class="row mb-3">
                            <label for="aa" class="col-md-4 col-form-label text-md-end">Anno Accademico</label>
                            <div class="col-12 col-md-8">
                                <select
                                    class="form-select"
                                    aria-label="Anno Accademico"
                                    id="aa"
                                    v-model="form.aa"
                                    required
                                    :disabled="!form.entrance"
                                    @change="changeAA"
                                >
                                    <option :value="null" disabled>Seleziona...</option>
                                    <option v-for="(aa, index) in helpers.aa" :key="index" :value="aa">
                                        {{ aa }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <!-- Piano di Studi -->
                        <div class="row mb-3">
                            <label for="careers" class="col-md-4 col-form-label text-md-end">Piano di Studi</label>
                            <div class="col-12 col-md-8">
                                <select class="form-select" aria-label="Piano di Studi" id="careers" v-model="form.pds" :disabled="!form.aa" required>
                                    <option :value="null" disabled>Seleziona...</option>
                                    <option v-for="(career, index) in helpers.careers" :key="index" :value="career.id">
                                        {{ career.label }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <hr/>

                        <!-- ECP -->
                        <div class="row mb-3">
                            <label for="ecp" class="col-md-4 col-form-label text-md-end">{{ university.ecp_text }}</label>
                            <div class="col-12 col-md-8">
                                <v-select
                                    :options="helpers.ecps"
                                    v-model="form.ecp"
                                    :clearable="false"
                                    :disabled="!!ecp || !form.aa"
                                    @input="getConventions">
                                </v-select>
                            </div>
                        </div>
                        <!-- Convenzione -->
                        <div class="row mb-3">
                            <label for="convention" class="col-md-4 col-form-label text-md-end">Convenzione</label>
                            <div class="col-12 col-md-8">
                                <v-select
                                    :options="helpers.conventions"
                                    v-model="form.convention"
                                    :clearable="false"
                                    :disabled="!form.aa || !!convention || validation.promo_code == 'success'"
                                    @input="getPrograms">
                                </v-select>
                            </div>
                        </div>
                        <!-- Programma -->
                        <div class="row mb-3" v-if="validation.promo_code_type != 'program'">
                            <label for="program" class="col-md-4 col-form-label text-md-end">Programma</label>
                            <div class="col-12 col-md-8">
                                <select
                                    class="form-select"
                                    aria-label="Programma"
                                    id="program"
                                    v-model="form.program"
                                    @change="()=>{getInstallments(); getLabels(); getImport()}"
                                    :disabled="!form.aa || !!program || validation.promo_code == 'success'"
                                    required>
                                        <option :value="null" disabled>Seleziona...</option>
                                        <option v-for="program in helpers.programs" :key="program.value" :value="{ id: program.id, label: program.label, promo_code: program.promo_code }">
                                            {{ program.label }}
                                        </option>
                                </select>
                            </div>
                        </div>
                        <!-- Rate -->
                        <div class="row mb-3">
                            <label for="installment" class="col-md-4 col-form-label text-md-end">Rate</label>
                            <div class="col-12 col-md-8">
                                <select
                                    class="form-select"
                                    aria-label="Rate"
                                    id="installment"
                                    v-model="form.installments"
                                    @change="getImport"
                                    :disabled="!form.aa"
                                    required
                                >
                                    <option :value="null" disabled>Seleziona...</option>
                                    <option v-for="installment in helpers.installments" :key="installment.value"
                                            :value="{ id: installment.id, label: installment.label }">
                                        {{ installment.label }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <!-- Programma Formativo -->
                        <div class="row mb-3">
                            <label for="label" class="col-md-4 col-form-label text-md-end">Programma Formativo</label>
                            <div class="col-12 col-md-8">
                                <select
                                    class="form-select"
                                    aria-label="Programma Formativo"
                                    id="label"
                                    v-model="form.label"
                                    :disabled="!form.program || !label"
                                    @change="getImport"
                                >
                                    <option value="">Nessuno</option>
                                    <option v-for="(label, index) in helpers.labels" :key="index" :value="label">
                                        {{ label }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <!-- Promo Code -->
                        <div class="row mb-3" v-if="form.program && form.program.promo_code">
                            <label for="promo-code" class="col-md-4 col-form-label text-md-end">Codice Promo</label>
                            <div class="col-12 col-md-8">
                                <div class="input-group">
                                    <input
                                        id="promo-code"
                                        type="text"
                                        v-model="form.promo_code"
                                        class="form-control"
                                        :disabled="!form.aa"
                                        :readonly="validation.promo_code == 'success'"
                                        :class="{ 'is-invalid': validation.promo_code === 'fail', 'is-valid': validation.promo_code === 'success' }">
                                    <button class="btn btn-outline-secondary" type="button" @click="getPromoCode" :disabled="validation.promo_code === 'success'">
                                        Applica
                                    </button>
                                    <div class="invalid-feedback" v-if="validation.promo_code === 'fail'">
                                        {{ validation.promo_code_message }}
                                    </div>
                                    <div v-else-if="validation.promo_code_message" class="text-success">
                                        <small>{{ validation.promo_code_message }}</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Codice MGM -->
                        <div class="row mb-3" v-if="university.crm == 'PEGASO'">
                            <label for="mgm-code" class="col-md-4 col-form-label text-md-end">
                                Codice "Porta un Amico"
                                <div class="custom-tooltip">
                                    <i class="fas fa-question-circle text-muted"></i>
                                    <div class="custom-tooltip-top border bg-white rounded shadow p-3">
                                        <p class="m-0">Il Codice "Porta un Amico" è un codice fornito mediante link di invito da uno studente iscritto
                                        all'Ateneo</p>
                                    </div>
                                </div>
                            </label>
                            <div class="col-12 col-md-8">
                                <div class="input-group">
                                    <input
                                        type="text"
                                        id="mgm-code"
                                        v-model="form.mgm_code"
                                        class="form-control"
                                        :disabled="!form.aa"
                                        :readonly="validation.mgm_code == 'success'"
                                        :class="{ 'is-invalid': validation.mgm_code === 'fail', 'is-valid': validation.mgm_code === 'success' }">
                                    <button
                                        class="btn btn-outline-secondary"
                                        type="button"
                                        @click="checkMgmCode"
                                        :disabled="validation.mgm_code === 'success'"
                                    >
                                        Applica
                                    </button>
                                    <div class="invalid-feedback" v-if="validation.mgm_code === 'fail'">
                                        {{ validation.mgm_code_message }}
                                    </div>
                                    <div v-else-if="validation.mgm_code_message" class="text-success">
                                        <small>{{ validation.mgm_code_message }}</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <hr/>

                        <!-- Assicurazione
                        <div class="row mb-3" v-if="assicurazione">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small">
                                    <input class="form-check-input" type="radio" v-model="form.insurance" id="insurance_n" :value="false">
                                    <label class="form-check-label" for="insurance_n">
                                        Dichiaro che il sottoscritto coincide con il soggetto titolare del pagamento delle rette accademiche
                                        e, pertanto, il soggetto assicurato, così come previsto dal CONTRATTO DI ASSICURAZIONE A GARANZIA
                                        DEL
                                        PAGAMENTO DELLE RETTE UNIVERSITARIE e le relative informative presenti al sito istituzionale di
                                        Ateneo
                                        al seguente <a :href="this.$store.getters['settings/site_url']" target="__blank">link</a>.
                                        I presenti dati anagrafici saranno utilizzati al fine dell’assicurazione in oggetto.
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="row mb-3" v-if="assicurazione">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small">
                                    <input class="form-check-input" type="radio" v-model="form.insurance" id="insurance_y" :value="true">
                                    <label class="form-check-label" for="insurance_y">
                                        Dichiaro di non essere il titolare del pagamento delle
                                        rette accademiche, pertanto il soggetto assicurato è persona
                                        diversa dal sottoscritto (INDICARE I DATI DI SEGUITO RICHIESTI
                                        DEL SOGGETTO ASSICURATO, OVVEROSIA TITOLARE DEL PAGAMENTO DELLE
                                        RETTE ACCADEMICHE).
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div :class="form.insurance ? 'd-block' : 'd-none'">
                            <div class="col-12 col-md-8 offset-md-4 font-bold mb-3"> Dati dell'assicurato</div>
                            <div class="row mb-3">
                                <label for="insurance_firstname" class="col-md-4 col-form-label text-md-end">Nome</label>
                                <div class="col-12 col-md-8">
                                    <input type="text" id="insurance_firstname" class="form-control" v-model="form.insurance_firstname">
                                </div>
                            </div>
                            <div class="row mb-3">
                                <label for="insurance_lastname" class="col-md-4 col-form-label text-md-end">Cognome</label>
                                <div class="col-12 col-md-8">
                                    <input type="text" id="insurance_lastname" class="form-control" v-model="form.insurance_lastname">
                                </div>
                            </div>
                            <div class="row mb-3">
                                <label for="insurance_fiscal_code" class="col-md-4 col-form-label text-md-end">Codice Fiscale</label>
                                <div class="col-12 col-md-8">
                                    <input type="text" id="insurance_fiscal_code" class="form-control" v-model="form.insurance_fiscal_code">
                                </div>
                            </div>
                            <div class="row mb-3">
                                <label for="insurance_birthdate" class="col-md-4 col-form-label text-md-end">Data di Nascita</label>
                                <div class="col-12 col-md-8">
                                    <date-picker
                                        v-model="form.insurance_birthdate"
                                        default-panel="year"
                                        valueType="format"
                                        class="w-100"
                                        placeholder="aaaa-mm-gg"
                                    />
                                </div>
                            </div>
                            <div class="row mb-3">
                                <label for="insurance_birthcity" class="col-md-4 col-form-label text-md-end">Città di Nascita</label>
                                <div class="col-12 col-md-8">
                                    <input type="text" id="insurance_birthcity" class="form-control" v-model="form.insurance_birthcity">
                                </div>
                            </div>
                        </div> -->

                        <!-- Contratto -->
                        <div class="row mb-3">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small">
                                    <input class="form-check-input" type="checkbox" v-model="form.privacy" id="privacy" value="1" required>
                                    <label class="form-check-label" for="privacy">
                                        <strong>Obbligatorio</strong> Approva e accetta espressamente, dopo averne presa attenta e
                                        specifica conoscenza e visione, le disposizioni degli artt. 6, 9, 10, 11 e 12, nonché
                                        della tabella dei diritti di segreteria e mora sui pagamenti
                                        <!-- (<a :href="university.contratto" title="Contratto" target="_blank">Contratto</a>) -->
                                    </label>
                                </div>
                            </div>
                        </div>
                        <!-- Privacy -->
                        <div class="row mb-3">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small">
                                    <input class="form-check-input" type="checkbox" v-model="form.privacy" id="privacy" value="1" required>
                                    <label class="form-check-label" for="privacy">
                                        <strong>Obbligatorio</strong> Autorizzo al trattamento dei dati personali per finalità connesse
                                        allo svolgimento delle attività istituzionali
                                        (<a :href="privacy" title="Contratto" target="_blank">Informativa</a>)
                                    </label>
                                </div>
                            </div>
                        </div>
                        <!-- Marketing -->
                        <div class="row">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small">
                                    <input class="form-check-input" type="checkbox" v-model="form.marketing" id="marketing" value="1">
                                    <label class="form-check-label" for="marketing">
                                        <strong>Facoltativo</strong> Autorizzo al trattamento dei dati per invio di materiale
                                        informativo (<a :href="privacy" title="Contratto" target="_blank">Informativa</a>)
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Validazione + Email -->
                        <div class="alert alert-danger mt-3" role="alert" v-if="!validation.enrol">
                            <h5><i class="fas fa-exclamation-triangle me-2"></i>Impossibile proseguire</h5>
                            <p class="mb-0">{{ validation.message }}</p>
                            <button type="button" class="btn btn-outline-danger mt-3" v-if="validation.sendEmail && !temp.sendedEmail" @click="sendEmailTemp">
                                <i class="fas fa-circle-notch fa-spin" v-if="temp.sendingEmail"></i>
                                <i class="far fa-envelope me-2" v-else></i>
                                Reinvia e-mail per l'accesso all'area personale
                            </button>
                        </div>
                        <div class="alert alert-success mt-3" v-if="temp.sendedEmail">
                            <h5><i class="fas fa-check-circle me-2"></i>E-Mail inviata</h5>
                            <p class="mb-0">Controlla la tua casella di posta elettronica.</p>
                        </div>

                    </div>
                    <div class="pt-3 text-end">
                        <button class="btn btn-success" type="submit" :disabled="helpers.sending">
                            Prosegui
                            <i class="fas fa-circle-notch fa-spin ms-2" v-if="helpers.sending"></i>
                            <i class="fas fa-angle-right ms-2" v-else></i>
                        </button>
                    </div>
                </form>
            </div>
        </transition>

        <!-- Step 2 -->
        <transition name="fade" mode="out-in">
            <div v-if="step == 2">
                <h4 class="mb-3">Dati Anagrafici</h4>
                <form @submit.prevent="submitStep2">
                    <div class="p-4 p-md-5 bg-white border rounded">
                        <!-- Sesso -->
                        <div class="row mb-3">
                            <label for="gender" class="col-md-4 col-form-label text-md-end">Sesso</label>
                            <div class="col-12 col-md-8">
                                <select class="form-select" aria-label="Sesso" id="gender" v-model="form.gender" required>
                                    <option :value="null" disabled>Seleziona</option>
                                    <option value="M">Maschio</option>
                                    <option value="F">Femmina</option>
                                </select>
                            </div>
                        </div>
                        <!-- Data di Nascita -->
                        <div class="row mb-3">
                        <label for="birthDate" class="col-md-4 col-form-label text-md-end">Data di Nascita</label>
                        <div class="col-12 col-md-8">
                            <date-picker
                                v-model="form.birth_date"
                                valueType="format"
                                class="w-100"
                                placeholder="aaaa-mm-gg"
                                default-panel="year"
                                :input-attr="{ required: 'required' }">
                            </date-picker>
                        </div>
                        </div>
                        <!-- Nazione di Nascita -->
                        <div class="row mb-3">
                        <label for="birthNation" class="col-md-4 col-form-label text-md-end">Nazione di Nascita</label>
                        <div class="col-12 col-md-8">
                            <v-select
                                :options="helpers.birth_nations"
                                v-model="form.birth_nation"
                                :clearable="false">
                            </v-select>
                        </div>
                        </div>
                        <!-- Provincia di Nascita -->
                        <div class="row mb-3" v-if="form.birth_nation.id == 'IT'">
                        <label for="birthProvince" class="col-md-4 col-form-label text-md-end">Provincia di Nascita</label>
                        <div class="col-12 col-md-8">
                            <v-select
                                :options="helpers.birth_provinces"
                                v-model="form.birth_province"
                                :clearable="false"
                                @input="changeBirthProvince"
                            >
                            <template #search="{attributes, events}">
                                <input class="vs__search" :required="!form.birth_province" v-bind="attributes" v-on="events"/>
                            </template>
                            </v-select>
                        </div>
                        </div>
                        <div class="row mb-3">
                        <label for="birthCity" class="col-md-4 col-form-label text-md-end">Comune di Nascita</label>
                        <div class="col-12 col-md-8">
                            <v-select :options="helpers.birth_cities" v-model="form.birth_city" :clearable="false"
                                    v-if="form.birth_nation.id == 'IT'">
                            <template #search="{attributes, events}">
                                <input class="vs__search" :required="!form.birth_city" v-bind="attributes" v-on="events"/>
                            </template>
                            </v-select>
                            <input type="text" class="form-control" v-model="form.birth_city" id="birthCity" v-else required>
                        </div>
                        </div>
                    </div>
                    <div class="pt-3 d-flex justify-content-between">
                        <button class="btn btn-secondary" type="button" @click="goBackStep">
                            <i class="fas fa-angle-left me-2"></i>Indietro
                        </button>
                        <button class="btn btn-success" type="submit">
                            Prosegui<i class="fas fa-angle-right ms-2"></i>
                        </button>
                    </div>
                </form>
            </div>
        </transition>

        <!-- Step 3 -->
        <div v-if="step == 3">
        <h4 class="mb-3">Recapiti</h4>
        <form @submit.prevent="submitStep3">
            <div class="p-4 p-md-5 bg-white border rounded">
            <div class="row mb-3">
                <label for="email" class="col-md-4 col-form-label text-md-end">E-Mail</label>
                <div class="col-12 col-md-8">
                <input type="email" class="form-control" id="email" v-model="form.email" required>
                </div>
            </div>
            <div class="row mb-3">
                <label for="email2" class="col-md-4 col-form-label text-md-end">Ripeti E-Mail</label>
                <div class="col-12 col-md-8">
                <input type="email" class="form-control" :class="{ 'is-invalid': !validation.email }" id="email2"
                        v-model="form.email_2" required>
                <div class="invalid-feedback" v-if="!validation.email">
                    La mail non corrisponde.
                </div>
                </div>
            </div>
            <div class="row mb-3">
                <label for="telephone" class="col-md-4 col-form-label text-md-end">Recapito Telefonico</label>
                <div class="col-12 col-md-8">
                <input type="text" class="form-control" id="telephone" v-model="form.telephone" required>
                </div>
            </div>
            <div class="row mb-3">
                <label for="addressNation" class="col-md-4 col-form-label text-md-end">Nazione di Residenza</label>
                <div class="col-12 col-md-8">
                <v-select :options="helpers.address_nations" v-model="form.address_nation" :clearable="false"></v-select>
                </div>
            </div>
            <div class="row mb-3" v-if="form.address_nation.id == 'IT'">
                <label for="addressProvince" class="col-md-4 col-form-label text-md-end">Provincia di Residenza</label>
                <div class="col-12 col-md-8">
                <v-select :options="helpers.address_provinces" v-model="form.address_province" :clearable="false"
                            @input="changeAddressProvince">
                    <template #search="{attributes, events}">
                    <input class="vs__search" :required="!form.address_province" v-bind="attributes" v-on="events"/>
                    </template>
                </v-select>
                </div>
            </div>
            <div class="row mb-3">
                <label for="addressCity" class="col-md-4 col-form-label text-md-end">Comune di Residenza</label>
                <div class="col-12 col-md-8">
                <v-select :options="helpers.address_cities" v-model="form.address_city" :clearable="false"
                            v-if="form.address_nation.id == 'IT'">
                    <template #search="{attributes, events}">
                    <input class="vs__search" :required="!form.address_city" v-bind="attributes" v-on="events"/>
                    </template>
                </v-select>
                <input type="text" class="form-control" v-model="form.address_city" id="addressCity" v-else required>
                </div>
            </div>
            <div class="row mb-3">
                <label for="address" class="col-md-4 col-form-label text-md-end">Indirizzo di Residenza</label>
                <div class="col-12 col-md-6">
                <input type="text" class="form-control" v-model="form.address" id="address" required>
                </div>
                <div class="col-12 col-md-2">
                <input type="text" class="form-control" v-model="form.address_number" placeholder="Civico" required>
                </div>
            </div>
            <div class="row mb-3">
                <label for="cap" class="col-md-4 col-form-label text-md-end">CAP</label>
                <div class="col-12 col-md-8">
                    <input type="text" class="form-control" v-model="form.cap" id="cap" @input="checkCAP" required :class="{'is-invalid': !validation.cap}">
                    <div class="invalid-feedback" v-if="!validation.cap">Formato non valido</div>
                </div>
            </div>
            <div class="alert alert-danger mt-3" role="alert" v-if="!validation.store">
                <h5><i class="fas fa-exclamation-triangle me-2"></i>Impossibile proseguire</h5>
                <p class="mb-0">Si è verificato un errore, contatta il supporto tecnico!</p>
            </div>
            </div>
            <div class="pt-3 d-flex justify-content-between">
            <button class="btn btn-secondary" @click="goBackStep">
                <i class="fas fa-angle-left me-2"></i>Indietro
            </button>
            <button class="btn btn-success" type="submit" :disabled="helpers.sending || !validation.cap">
                Prosegui
                <i class="fas fa-circle-notch fa-spin ms-2" v-if="helpers.sending"></i>
                <i class="fas fa-angle-right ms-2" v-else></i>
            </button>
            </div>
        </form>
        </div>

        <!-- Step 4 -->
        <div v-if="step == 4">
        <h4 class="mb-3">Area personale</h4>
        <div class="p-4 p-md-5 bg-white border rounded">
            <p>Gentile <strong>{{ form.first_name }} {{ form.last_name }}</strong>,</p>
            <p>
            la sua richiesta è andata a buon fine!<br/>
            Per poter completare la sua iscrizione le chiediamo di caricare i documenti richiesti
            nella sua pagina personale.
            </p>
            <a :href="access_link" target="_blank" class="btn btn-success">
            <i class="fas fa-sign-in-alt me-2"></i>Accedi all'area personale
            </a>
        </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios'
    import vSelect from 'vue-select'
    import DatePicker from 'vue2-datepicker'
    import * as services from '../../services';

    export default {
    props: {
        step: {
            type: Number
        },
        course: {
            type: Object,
            required: false
        }
    },
    components: {
        vSelect,
        DatePicker
    },
    data() {
        return {
            form: {
                course: null,
                first_name: null,
                last_name: null,
                fiscal_code: null,
                entrance: null,
                doppia_laurea: null,
                doppia_laurea_ateneo: null,
                aa: null,
                pds: null,
                label: '',
                ecp: null,
                convention: null,
                program: null,
                installments: null,
                mgm_code: null,
                prospect: null,
                prospect_next: null,
                privacy: 1,
                marketing: 0,
                gender: null,
                birth_date: null,
                birth_nation: {'label': 'ITALIA', 'id': 'IT'},
                birth_province: null,
                birth_city: null,
                address: null,
                address_number: null,
                address_nation: {'label': 'ITALIA', 'id': 'IT'},
                address_province: null,
                address_city: null,
                cap: null,
                promo_code: null,
                supplement_program_id: null,
                insurance: false,
                insurance_firstname: null,
                insurance_lastname: null,
                insurance_fiscal_code: null,
                insurance_birthdate: null,
                insurance_birthcity: null,
            },
            helpers: {
                courses: [],
                entrances: [],
                universities: [],
                aa: [],
                careers: [],
                labels: [],
                ecps: [],
                conventions: [],
                programs: [],
                installments: [],
                birth_nations: [],
                birth_provinces: [],
                birth_cities: [],
                address_nations: [],
                address_provinces: [],
                address_cities: [],
                sending: false
            },
            validation: {
                email: true,
                fiscal_code: true,
                enrol: true,
                message: null,
                sendEmail: false,
                store: true,
                promo_code: null,
                promo_code_message: null,
                promo_code_type: null,
                mgm_code: null,
                mgm_code_message: null,
                cap: true
            },
            temp: {
                sendingEmail: false,
                sendedEmail: false
            },
            access_link: null
        }
    },
    computed: {
        assicurazione() {
            return this.$store.getters['settings/assicurazione']
        },
        user_id() {
            return this.$store.getters['settings/user_id']
        },
        first_name() {
            return this.$store.getters['settings/first_name']
        },
        last_name() {
            return this.$store.getters['settings/last_name']
        },
        fiscal_code() {
            return this.$store.getters['settings/fiscal_code']
        },
        university() {
            return this.$store.getters['settings/from']
        },
        token() {
            return this.$store.getters['settings/token']
        },
        ecp() {
            return this.$store.getters['settings/ecp']
        },
        ecp_code() {
            return this.$store.getters['settings/ecp_code']
        },
        convention() {
            return this.$store.getters['settings/convention']
        },
        program() {
            return this.$store.getters['settings/program']
        },
        label() {
            return this.$store.getters['settings/label']
        },
        tracking() {
            return this.$store.getters['settings/tracking']
        },
        provider() {
            return this.$store.getters['settings/provider']
        },
        gads_conversion() {
            return this.$store.getters['settings/gads_conversion']
        },
        floodlight_conversion() {
            return this.$store.getters['settings/floodlight_conversion']
        },
        criteo_conversion() {
            return this.$store.getters['settings/criteo_conversion']
        },
        crm() {
            return this.$store.getters['settings/crm']
        },
        privacy() {
            return this.$store.getters['settings/site_url'] + '/privacy'
        },
        campaign() {
            return this.$store.getters['settings/campaign']
        }
    },
    methods: {
        async getCourses() {
            const data = await services.ecp.post('api/get/graduate/courses')
            this.helpers.courses = data.data.map(el => {
                return {
                    name: el.name,
                    code: el.code
                }
            })
        },
        async changeCourse() {
            this.$store.dispatch('summary/setCourse', this.form.course.name)
            this.form.entrance = null
            this.form.pds = null
            this.form.aa = null
            await this.getEntrance()
            await this.getUniversities()
            await this.getAcademicYear()
            await this.getCareers()
            await this.getConventions()
        },
        async getEntrance() {
            const data = await services.ecp.post('api/get/graduate/tipo/ingresso', {
                courseCode: this.form.course.code
            });
            this.helpers.entrances = data.data.map(el => {
                return {
                label: el.DESCRIZIONE,
                id: el.ID_TIPO_INGRESSO
                }
            })
        },
        async getUniversities() {
            const data = await services.ecp.get('api/atenei')
            this.helpers.universities = data.data.map(({ID_UNIVERSITA, nome}) => ({id: ID_UNIVERSITA, label: nome}));
        },
        async getAcademicYear() {
            const data = await services.ecp.post('api/get/graduate/academicYear/currents', {
                course_code: this.form.course.code
            })
            this.helpers.aa = data.data
        },
        async getLabels(){
            const labels = await services.ecp.post('api/get/graduate/labels', {
                course_code: this.form.course.code,
                academicYear: this.form.aa,
                program_id:this.form.program.id,
            })
            this.helpers.labels = labels.data
        },
        async getCareers() {
            this.helpers.careers = null
            this.helpers.labels = null
            const careers = await services.ecp.post('api/get/graduate/careers', {
                course_code: this.form.course.code,
                academicYear: this.form.aa
            })
            this.helpers.careers = careers.data.map(el => {
                return {
                label: el.name,
                id: el.id
                }
            })
            await this.getImport()
        },
        async changeAA() {
            await this.getCareers()
            await this.getConventions()
            await this.getPrograms()
        },
        // async changeLabel() {
        //   await this.getConventions()
        //   await this.getImport()
        // },
        async getEcp() {
            const data = await services.ecp.get('api/get/ecp-active-no-master')
            this.helpers.ecps = data.data.map(el => {
                return {
                    'label': el.codice.toUpperCase(),
                    'id': el.user_id
                }
            })
            if (!this.ecp && !this.ecp_code) {
                this.form.ecp = {
                    label: 'DIREZIONALE',
                    id: this.university.direzionale_id
                }
            } else if (this.ecp_code) {
                this.form.ecp = this.helpers.ecps.find(el => el.label == this.ecp_code)
            } else {
                this.form.ecp = this.helpers.ecps.find(el => el.id == this.ecp)
            }
        },
        async getPromoCode() {
            if (!this.form.fiscal_code) {
                this.validation.promo_code = 'fail'
                this.validation.promo_code_message = 'E\' necessario il codice fiscale'
                return
            }
            if (!this.form.promo_code) {
                this.validation.promo_code = null
                this.validation.promo_code_message = null
                return
            }
            const data = await services.ecp.post('api/check-promo-code', {
                promo_code: this.form.promo_code,
                fiscal_code: this.form.fiscal_code,
                course_code: this.form.course.code,
                convention_id: this.form.convention.id,
                program_id: this.form.program.id,
                academicYear: this.form.aa,
            });

            this.helpers.labels = [];
            this.form.label = '';

            this.form.supplement_program_id = null
            //this.form.program = null
            const el = data.data
            this.validation.promo_code = el.status
            this.validation.promo_code_message = el.message
            if (el.status == 'fail') {
                this.validation.promo_code_message = 'Codice promo non valido'
            } else {
                this.validation.promo_code_type = el.type
            }
            if (el.type == 'SupplementProgram') {
                this.form.supplement_program_id = el.data.id
                if (el.data.pds_list && el.data.pds_list.length > 0) {
                    this.form.pds = el.data.pds_list;
                }
                if (el.data.disable_classform) {
                    this.helpers.labels = [];
                    this.form.label = '';
                }
            }
            if (el.type == 'program') {
                this.form.program = {
                    id: el.data.id,
                    label: el.data.name
                }
            }


            if (el.type == 'supplementAndProgram') {

              const supplementProgram = el.data.supplementProgram;
              const program = el.data.program;

              this.form.supplement_program_id = supplementProgram.id
              if (supplementProgram.pds_list && supplementProgram.pds_list.length > 0) {
                this.form.pds = supplementProgram.pds_list;
              }
              if (supplementProgram.disable_classform) {
                this.helpers.labels = [];
                this.form.label = '';
              }

              this.form.program = {
                id: program.id,
                label: program.name
              }
            }

            await this.$nextTick()
            this.form.prospect = null
            this.getInstallments()
        },

        async getConventions() {
            const data = await services.ecp.post('api/get/graduate/conventions', {
                ecp_id: this.form.ecp.id
            })
            this.helpers.conventions = data.data.map(el => {
                return {
                    label: el.name.toUpperCase(),
                    id: el.id
                }
            })
            if (!this.convention) {
                this.form.convention = this.helpers.conventions.find(el => el.label == 'STANDARD' || el.label == 'STANDARD-UTP' || el.label == 'STANDARD-UTSR')
            } else {
                this.form.convention = this.helpers.conventions.find(el => el.id == this.convention)
            }
            if (this.form.course && this.form.course.code) {
                this.getPrograms()
            }
        },
        async getPrograms() {
            if (this.validation.promo_code_type == 'program') return
            this.form.program = null
            this.form.prospect = null
            this.helpers.installments = []
            const data = await services.ecp.post('api/get/graduate/programs', {
                convention_id: this.form.convention.id,
                label: this.form.label,
                academicYear: this.form.aa,
                category_code: this.form.course ? this.form.course.code : null
            })
            this.helpers.programs = data.data.map(el => {
                return {
                    label: el.name.toUpperCase(),
                    id: el.id,
                    promo_code: !!el.promo_code,
                }
            })
            if (this.program) {
                this.form.program = this.helpers.programs.find(el => el.id == this.program)
            }
            //this.getInstallments()
        },
        async getInstallments() {
            this.helpers.installments = []
            this.form.installments = null
            const data = await services.ecp.post('api/get/graduate/installments', {
                convention_id: this.form.convention.id,
                program_id: this.form.program.id
            })
            this.helpers.installments = data.data.map(el => {
                return {
                    label: el.num_rate + ' - ' + el.name,
                    id: el.id
                }
            })
        },
        async getImport() {
            const aa = this.form.aa
            const label = this.form.label
            const program = this.form.program
            const installments = this.form.installments
            const course_code = this.form.course.code
            const convention_id = this.form.convention.id
            const promo_code = this.form.promo_code
            const fiscal_code = this.form.fiscal_code

            if (aa && program && installments) {
                const data = await services.ecp.post('api/get/graduate/prospectus', {
                    academicYear: aa,
                    label: label,
                    program: program.id,
                    installments: installments.id,
                    course_code,
                    convention_id,
                    promo_code,
                    fiscal_code,
                })
                this.form.prospect = data.data
                const prospectus_next = await services.ecp.post('api/get/graduate/sub-prospectus', {
                    academicYear: aa,
                    label: label,
                    program: program.id,
                    installments: installments.id,
                    course_code,
                    convention_id,
                    promo_code,
                    fiscal_code,
                })
                this.form.prospect_next = prospectus_next.data;
            }
        },
        async getNations() {
            const data = await services.ecp.post('api/get/nations')
            return data.data.map(el => {
                return {
                    'label': el.NAZIONE,
                    'id': el.ID_NAZIONALITA
                }
            })
        },
        async getProvinces() {
            const data = await services.ecp.post('api/get/province')
            return data.data.map(el => {
                return {
                    'label': el.PROVINCIA,
                    'id': el.SIGLA
                }
            })
        },
        async changeBirthProvince() {
            const province_id = this.form.birth_province.id
            this.form.birth_city = null
            this.helpers.birth_cities = await this.getCities(province_id)
        },
        async changeAddressProvince() {
            const province_id = this.form.address_province.id
            this.form.address_city = null
            this.helpers.address_cities = await this.getCities(province_id)
        },
        async getCities(province_id) {
            const data = await services.ecp.post('api/get/cities', {
                province: province_id
            })
            return data.data.map(el => {
                return {
                    'label': el.COMUNE,
                    'id': el.COD_ISTAT
                }
            })
        },
        async checkMgmCode() {
            try {
                const { data } = await services.ecp.post('api/check-mgm-code', {
                    mgm_code: this.form.mgm_code,
                    fiscal_code: this.form.fiscal_code
                });
                this.validation.mgm_code = 'success';
                this.form.ecp = this.helpers.ecps.find(el => el.id == data.ecp_id);
                this.$store.dispatch('settings/setEcp', data.ecp_id);
            } catch (_) {
                console.log(_);
                this.validation.mgm_code = 'fail';
                this.validation.mgm_code_message = 'Il codice inserito non è valido';
            }
        },
        checkCAP() {
            if (this.form.cap == '') {
                this.validation.cap = true
                return
            }
            let pattern = new RegExp('^[0-9]{5,5}$');
            if (!this.form.cap.match(pattern)) {
                this.validation.cap = false
            } else {
                this.validation.cap = true
            }
        },
        goBackStep() {
            const step = this.step - 1
            this.$emit('update', step)
        },
        async submitStep1() {
            this.helpers.sending = true
            /*try {
                const check_fc = await axios.post('https://aws-gammaapi.multiversity.click/check-name-fiscal-code/eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOjE4NTE2LCJpc3MiOiJQRUdBU08i', {
                fiscalCode: this.form.fiscal_code,
                name: this.form.first_name,
                surname: this.form.last_name
                }, {
                headers: {
                    'Content-Type': 'application/json'
                }
                })
                this.validation.fiscal_code = check_fc.data.data
            } catch (error) {
                this.validation.fiscal_code = false
            }*/
            try {
                const check_enrol = await services.ecp.post('api/get/graduate/fiscal/code/check', {
                    courseCode: this.form.course.code,
                    fiscalCode: this.form.fiscal_code,
                    academicYear: this.form.aa
                })
                this.validation.enrol = true
            } catch (error) {
                this.validation.enrol = false
                const response = error.response
                switch (response.status) {
                    case 409:
                        this.validation.message = response.data
                        this.validation.sendEmail = true
                        break
                    case 403:
                        this.validation.message = response.data
                        break
                    case 410:
                        this.validation.message = 'Studente proveniente da MASTER LM. Abbiamo inviato una e-mail allo studente contentente l\'indirizzo per il caricamento dei documenti necessari all\'iscrizione'
                        break
                    default:
                        this.validation.message = response.data
                        break
                }
            }
            this.helpers.sending = false
            this.validation.email = this.form.email == this.form.email_2
            if (this.validation.fiscal_code && this.validation.enrol && this.validation.email) {
                if (this.tracking === true && this.$cookieconsent.acceptedCategory('marketing')) {
                //this.trackFacebookAddToCart(this.course.code)
                //this.$trackSnapChatAddCart('CDL', this.form.course.code)
                //this.$trackCriteoAddCart(this.criteo_conversion, this.form.course.code, this.form.prospect.program.import)
                }
                const step = this.step + 1
                this.$emit('update', step)
            }
        },
        async sendEmailTemp() {
            this.temp.sendingEmail = true
            try {
                const data = await services.ecp.post('api/send/graduate/enrolment/online/mail', {
                    courseCode: this.form.course.code,
                    fiscalCode: this.form.fiscal_code
                })
                this.temp.sendingEmail = false
                this.temp.sendedEmail = true
            } catch (error) {
                this.temp.sendingEmail = false
                console.log(error)
            }
        },
        submitStep2() {
            const step = this.step + 1
            this.$emit('update', step)
        },
        async submitStep3() {
            this.helpers.sending = true
            const form = this.form
            const birth_date = form.birth_date.split('-')
            let request = {}
            let crm_id = 0
            let sendToEnrol = {}
            let storeInCrm = true

            // crm
            if (this.crm === true) {
                try {
                    const crmFields = {
                        from: this.university.crm,
                        first_name: form.first_name,
                        last_name: form.last_name,
                        email: form.email,
                        telephone: form.telephone,
                        fiscal_code: form.fiscal_code,
                        birth_date: form.birth_date,
                        gender: form.gender,
                        province: form.address_nation.id == 'IT' ? form.address_province.label : form.address_province,
                        city: form.address_nation.id == 'IT' ? form.address_city.label : form.address_city,
                        type: 4,
                        category: 2,
                        subject: form.course.name,
                        provider: this.provider,
                        campaign: this.campaign ? this.campaign : this.$cookie('CRM_campaign', '(not set)'),
                        privacy: 1,
                        marketing: form.marketing,
                        ga_id: this.$cookie('_ga', null),
                        gclid: this.$gclid(),
                        hubspotutk: this.$cookie('hubspotutk', null),
                        code: form.course.code,
                        enrol_type: form.entrance.label,
                        classform: form.label === '' ? 0 : 1,
                        ecp: form.ecp.label,
                        convention: form.convention.label,
                        program: form.program.label,
                        import: form.prospect.program.import
                    }
                    const sendToCrm = await services.crm.post('crm/public/request', crmFields)
                    request = sendToCrm.data.data.request
                    crm_id = sendToCrm.data.data.request.id
                    storeInCrm = true 
                } catch (error) {
                    storeInCrm = false
                    console.log(error)
                }
            }

            // segreteria
            if (storeInCrm) {
                try {
                    const enrolFields = {
                        courseCode: form.course.code,
                        ecp: form.ecp.id,
                        academicYear: form.aa,
                        tipologia: form.entrance.id,
                        doppia_laurea_ateneo: form.doppia_laurea_ateneo?.label,
                        doppia_laurea_id: form.doppia_laurea_ateneo?.id,
                        pds: form.pds,
                        label: form.label,
                        convention: form.convention.id,
                        program: form.program.id,
                        installments: form.installments.id,
                        firstname: form.first_name,
                        lastname: form.last_name,
                        fiscalCode: form.fiscal_code,
                        email: form.email,
                        email_confirmation: form.email_2,
                        phone: form.telephone,
                        gender: form.gender,
                        birthdate: birth_date[2] + '/' + birth_date[1] + '/' + birth_date[0],
                        birthnation: form.birth_nation.id,
                        birthprovince: form.birth_nation.id == 'IT' ? form.birth_province.id : null,
                        birthcity: form.birth_nation.id == 'IT' ? form.birth_city.id : form.birth_city,
                        address: form.address,
                        address_n: form.address_number,
                        nation: form.address_nation.id,
                        province: form.address_nation.id == 'IT' ? form.address_province.id : null,
                        city: form.address_nation.id == 'IT' ? form.address_city.id : form.address_city,
                        cap: form.cap,
                        crm_id: crm_id,
                        supplement_program_id: form.supplement_program_id,
                        promo_code: form.promo_code,
                        insurance_firstname: form.insurance_firstname,
                        insurance_lastname: form.insurance_lastname,
                        insurance_fiscal_code: form.insurance_fiscal_code,
                        insurance_birthdate: form.insurance_birthdate,
                        insurance_birthcity: form.insurance_birthcity,
                        mgm_code: this.validation.mgm_code === 'success' ? form.mgm_code : undefined,
                        user_id: this.user_id,
                    }
                    sendToEnrol = await services.ecp.post('api/send/graduate/enrolment/online/web', enrolFields)
                    this.validation.store = true
                    this.access_link = 'https://' + this.university.api + '.multiversity.click/main/online/graduate_registration.php?online_token=' + sendToEnrol.data.token
                } catch (error) {
                    this.validation.store = false
                    console.log(error)
                }
            } else {
                this.validation.store = false
            }

            // tracking
            if (this.tracking === true && storeInCrm && this.validation.store) {
                if (this.$cookieconsent.acceptedCategory('analytics')) {
                    this.$trackGoogleAnalyticsBeginCheckout('CDL', form.prospect.program.import)
                }
                if (this.$cookieconsent.acceptedCategory('marketing')) {
                    if (this.gads_conversion) {
                        this.$trackGoogleAdsConversion(this.gads_conversion, 'CDL', form.prospect.program.import, form.course.name.substring(0, 100))
                    }
                    if (this.floodlight_conversion) {
                        this.$trackFloodlightConversion(this.floodlight_conversion, crm_id, 'CDL', form.course.name.substring(0, 100), form.prospect.program.import, form.gender, form.birth_date, form.ecp.label, form.entrance, this.$ga_id(), request.cid)
                    }
                    this.$trackFacebookCompleteRegistration(crm_id)
                    this.$trackBingSignUp('CDL', form.prospect.program.import)
                }
            }
            
            this.helpers.sending = false
            if (this.validation.store) {
                const step = this.step + 1
                this.$emit('update', step)
            }
        }
    },
        async mounted() {
            if (!this.course) {
                await this.getCourses()
            } else {
                this.form.course = this.course
                this.$store.dispatch('summary/setCourse', this.course.name)
                await this.getAcademicYear()
                await this.getEntrance()
            }
            await this.getUniversities()
            await this.getEcp()
            await this.getConventions()
            const nations = await this.getNations()
            const provinces = await this.getProvinces()
            this.helpers.birth_nations = nations
            this.helpers.address_nations = nations
            this.helpers.birth_provinces = provinces
            this.helpers.address_provinces = provinces
            if (this.first_name) this.form.first_name = this.first_name
            if (this.last_name) this.form.last_name = this.last_name
            if (this.fiscal_code) this.form.fiscal_code = this.fiscal_code
        },
        watch: {
            'form.doppia_laurea'(val) {
                this.form.doppia_laurea_ateneo = null;
            },
            'form.insurance'(val) {
                if (!val) {
                    this.form.insurance_firstname = null;
                    this.form.insurance_lastname = null;
                    this.form.insurance_birthdate = null;
                    this.form.insurance_birthcity = null;
                    this.form.insurance_fiscal_code = null;
                }
            },
            'form.ecp'(val) {
                this.$store.dispatch('summary/setEcp', val.label)
            },
            'form.convention'(val) {
                this.$store.dispatch('summary/setConvention', val.label)
            },
            'form.program'(val) {
                const program = val ? val.label : val
                this.$store.dispatch('summary/setProgram', program)
            },
            'form.prospect'(val) {
                if (val) {
                    this.$store.dispatch('summary/setRate', val.prospectus.length)
                    this.$store.dispatch('summary/setImport', val.program.import)
                    this.$store.dispatch('summary/setInstallments', val.prospectus)
                } else {
                    this.$store.dispatch('summary/setRate', null)
                    this.$store.dispatch('summary/setImport', null)
                    this.$store.dispatch('summary/setInstallments', null)
                }
            },
            'form.prospect_next'(val) {
                if (val) {
                    this.$store.dispatch('summary/setInstallmentsNext', val.prospectus)
                } else {
                    this.$store.dispatch('summary/setInstallmentsNext', null)
                }
            },
            'form.course'(val, old) {
                if (val && val.code !== old?.code) {
                    this.form.promo_code = null
                    this.validation.promo_code = null,
                        this.validation.promo_code_message = null
                    this.validation.promo_code_type = null
                }
            }
        }
    }
</script>